// Stripe Billing Provider Adapter
// Implements BillingProvider interface for Stripe

import { loadStripe, Stripe, StripeEmbeddedCheckout, PaymentRequest } from '@stripe/stripe-js';
import {
  BaseBillingProvider,
  BillingProviderConfig,
  CheckoutOptions,
  CheckoutResult,
  CheckoutMode,
  PriceInfo
} from '../domain/billing-provider';
import { getStripePriceId } from '../config/stripe-prices';

export class StripeAdapter extends BaseBillingProvider {
  readonly name = 'stripe' as const;
  private stripe: Stripe | null = null;
  private publicKey: string = '';
  private environment: 'sandbox' | 'production' = 'sandbox';
  private checkoutSession: any = null;
  private popup: HTMLElement | null = null;
  private embeddedCheckout: StripeEmbeddedCheckout | null = null;
  private paymentRequest: PaymentRequest | null = null;

  async initialize(config: BillingProviderConfig): Promise<void> {
    if (this.initialized) {
      return;
    }

    this.environment = config.environment;
    this.publicKey = config.publicKey || '';

    if (!this.publicKey) {
      throw new Error('Stripe public key is required');
    }

    // Load Stripe using the official SDK
    try {
      this.stripe = await loadStripe(this.publicKey);

      if (!this.stripe) {
        throw new Error('Failed to load Stripe');
      }

      this.initialized = true;
      console.log('[StripeAdapter] Initialized in', this.environment, 'mode');
    } catch (error) {
      console.error('[StripeAdapter] Failed to initialize:', error);
      throw new Error(`Failed to initialize Stripe: ${error}`);
    }
  }

  async fetchPrices(priceIds: string[]): Promise<PriceInfo[]> {
    if (!this.initialized) {
      throw new Error('StripeAdapter not initialized');
    }

    // Map product IDs to Stripe price IDs
    const stripePriceIds = priceIds
      .map(id => getStripePriceId(id))
      .filter(id => id !== null);

    if (stripePriceIds.length === 0) {
      console.warn('[StripeAdapter] No valid Stripe price IDs found');
      return [];
    }

    // Note: Stripe doesn't have a direct client-side price preview API
    // Prices typically come from your backend
    // This is a mock implementation - replace with actual backend call
    try {
      const response = await fetch('/api/stripe/prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceIds: stripePriceIds })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch prices');
      }

      const data = await response.json();
      const prices: PriceInfo[] = data.prices.map((price: any) => ({
        productId: price.id,
        amount: price.unit_amount / 100, // Stripe uses cents
        currency: price.currency.toUpperCase(),
        currencySymbol: this.getCurrencySymbol(price.currency.toUpperCase()),
        tax: price.tax || 0,
        total: (price.unit_amount / 100) + (price.tax || 0),
        formatted: {
          amount: this.formatPrice(price.unit_amount / 100, price.currency),
          tax: this.formatPrice(price.tax || 0, price.currency),
          total: this.formatPrice((price.unit_amount / 100) + (price.tax || 0), price.currency)
        }
      }));

      this.emit('prices:updated', prices);
      return prices;
    } catch (error) {
      console.error('[StripeAdapter] Failed to fetch prices:', error);
      // Return mock prices for development
      return this.getMockPrices(stripePriceIds);
    }
  }

  private getMockPrices(priceIds: string[]): PriceInfo[] {
    // Mock prices for development
    return priceIds.map(id => ({
      productId: id,
      amount: 39.99,
      currency: 'USD',
      currencySymbol: '$',
      tax: 0,
      total: 39.99,
      formatted: {
        amount: '$39.99',
        tax: '$0.00',
        total: '$39.99'
      }
    }));
  }

  async openCheckout(options: CheckoutOptions, mode: CheckoutMode): Promise<void> {
    if (!this.initialized) {
      throw new Error('StripeAdapter not initialized');
    }

    // Get Stripe price ID
    const stripePriceId = getStripePriceId(options.priceId);
    if (!stripePriceId) {
      throw new Error(`No Stripe price ID found for ${options.priceId}`);
    }

    if (mode === 'inline') {
      await this.openInlineCheckout(stripePriceId, options);
    } else if (mode === 'popup') {
      await this.openPopupCheckout(stripePriceId, options);
    } else {
      await this.openRedirectCheckout(stripePriceId, options);
    }
  }

  private async openInlineCheckout(priceId: string, options: CheckoutOptions): Promise<void> {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    // Create popup for inline checkout
    this.createCheckoutPopup();

    try {
      // Create checkout session via backend
      const session = await this.createCheckoutSession(priceId, options);

      // Initialize embedded checkout with proper typing
      this.embeddedCheckout = await this.stripe.initEmbeddedCheckout({
        clientSecret: session.clientSecret
      });

      // Mount to container
      await this.embeddedCheckout.mount('#checkout-container');

      this.emit('checkout:opened', undefined);
    } catch (error) {
      console.error('[StripeAdapter] Failed to open inline checkout:', error);
      this.closePopupIfExists();
      this.emit('checkout:error', { error: String(error) });
    }
  }

  private async openPopupCheckout(priceId: string, options: CheckoutOptions): Promise<void> {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    try {
      // Create checkout session via backend
      const session = await this.createCheckoutSession(priceId, options);

      // Redirect to Stripe hosted checkout
      const { error } = await this.stripe.redirectToCheckout({
        sessionId: session.id
      });

      if (error) {
        throw error;
      }

      this.emit('checkout:opened', undefined);
    } catch (error) {
      console.error('[StripeAdapter] Failed to open popup checkout:', error);
      this.emit('checkout:error', { error: String(error) });
    }
  }

  private async openRedirectCheckout(priceId: string, options: CheckoutOptions): Promise<void> {
    // Same as popup for Stripe (always redirects)
    await this.openPopupCheckout(priceId, options);
  }

  private async createCheckoutSession(priceId: string, options: CheckoutOptions): Promise<any> {
    // This should call your backend to create a Stripe checkout session
    // Mock implementation - replace with actual backend call
    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          email: options.email,
          metadata: options.customData,
          successUrl: options.successUrl || `${window.location.origin}/success`,
          cancelUrl: options.cancelUrl || window.location.href,
          discountCode: options.discountCode
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      return await response.json();
    } catch (error) {
      console.error('[StripeAdapter] Failed to create checkout session:', error);
      // Return mock session for development
      return {
        id: 'cs_test_' + Math.random().toString(36).substr(2, 9),
        clientSecret: 'cs_test_secret_' + Math.random().toString(36).substr(2, 9)
      };
    }
  }

  private createCheckoutPopup(): void {
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'checkout-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    // Create popup container
    const popup = document.createElement('div');
    popup.className = 'checkout-popup';
    popup.style.cssText = `
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      max-width: 600px;
      width: 90%;
      max-height: 90vh;
      position: relative;
      overflow: auto;
    `;

    // Create close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: none;
      border: none;
      font-size: 30px;
      cursor: pointer;
      z-index: 1;
      color: #666;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    closeBtn.onclick = () => this.closeCheckout();

    // Create container for Stripe embedded checkout
    const container = document.createElement('div');
    container.id = 'checkout-container';
    container.style.cssText = `
      width: 100%;
      min-height: 500px;
      padding: 20px;
    `;

    // Assemble popup
    popup.appendChild(closeBtn);
    popup.appendChild(container);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);

    this.popup = overlay;

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeCheckout();
      }
    });
  }

  private closePopupIfExists(): void {
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
  }

  private handleCheckoutComplete(sessionId: string): void {
    const result: CheckoutResult = {
      status: 'success',
      transactionId: sessionId
    };
    this.emit('checkout:closed', result);
    this.emit('checkout:success', { transactionId: sessionId });
    this.closePopupIfExists();
  }

  closeCheckout(): void {
    if (this.embeddedCheckout) {
      this.embeddedCheckout.destroy();
      this.embeddedCheckout = null;
    }
    this.closePopupIfExists();

    const result: CheckoutResult = { status: 'cancelled' };
    this.emit('checkout:closed', result);
    this.emit('checkout:cancelled', undefined);
  }

  async isApplePayAvailable(): Promise<boolean> {
    if (!this.initialized || !this.stripe) {
      return false;
    }

    try {
      // Create payment request only once and reuse
      if (!this.paymentRequest) {
        this.paymentRequest = this.stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Test',
            amount: 100
          },
          requestPayerName: false,
          requestPayerEmail: false
        });
      }

      const result = await this.paymentRequest.canMakePayment();
      return result !== null && result.applePay === true;
    } catch (error) {
      console.error('[StripeAdapter] Failed to check Apple Pay availability:', error);
      return false;
    }
  }

  async isGooglePayAvailable(): Promise<boolean> {
    if (!this.initialized || !this.stripe) {
      return false;
    }

    try {
      // Create payment request only once and reuse
      if (!this.paymentRequest) {
        this.paymentRequest = this.stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'Test',
            amount: 100
          },
          requestPayerName: false,
          requestPayerEmail: false
        });
      }

      const result = await this.paymentRequest.canMakePayment();
      return result !== null && result.googlePay === true;
    } catch (error) {
      console.error('[StripeAdapter] Failed to check Google Pay availability:', error);
      return false;
    }
  }

  private getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      KRW: '₩',
      BRL: 'R$',
      MXN: '$',
      // Add more as needed
    };
    return symbols[currency] || currency;
  }

  destroy(): void {
    this.closeCheckout();
    if (this.paymentRequest) {
      this.paymentRequest = null;
    }
    this.stripe = null;
    super.destroy();
  }
}